import * as React from "react";

const POLL_INTERVAL_MS = 3 * 60 * 1_000;

export const useVersionPolling = () => {
  const [showModal, setShowModal] = React.useState(false);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const initialEtagRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (import.meta.env.DEV) return;

    const checkVersion = async () => {
      try {
        const response = await fetch("/index.html", {
          method: "HEAD",
          cache: "no-store",
        });
        if (!response.ok) return;
        const etag = response.headers.get("etag");
        if (!etag) return;

        if (!initialEtagRef.current) {
          initialEtagRef.current = etag;
          return;
        }

        if (etag !== initialEtagRef.current) {
          setShowModal(true);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch {
        // intentional: best-effort polling
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (!intervalRef.current) {
        checkVersion();
        intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);
      }
    };

    checkVersion();
    intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const dismiss = React.useCallback(() => {
    setShowModal(false);
  }, []);

  return { isNewVersionAvailable: showModal, dismiss };
};
